<!-- 
Testing tools page.
This won't be visible in the production environment.
-->
<template>
  <div :class="css">
    <our-heading 
      :breadcrumbs="breadcrumbs"
      title="Testing tools"
      user-guide-topic="common"
      user-guide-section="testing-tools"
    >
      <template v-if="!isSignedIn" v-slot:navigator >
        <v-btn flat color="primary" to="/sign-in">
          <v-icon class="mr-2">person_outline</v-icon>
          Sign in
        </v-btn>
      </template>
    </our-heading>
    <v-card class="home">
      <v-tabs v-model="activeTab" slider-color="primary" show-arrows>
        <v-tab key="rebuild" href="#">
          Rebuild
        </v-tab>
        <v-tab key="theme" href="#">
          Theme
        </v-tab>
        <v-tab key="users" href="#">
          Users
        </v-tab>
        <v-tab key="other" href="#">
          Other
        </v-tab>
        <v-tab-item key="rebuild" transition="none" reverse-transition="none">
          <v-card flat>
            <v-card-text>
              <h3 class="title mb-3">Database rebuild</h3>
              <p>
                A database rebuild will delete most data from the database and reseed it with a fresh set. Only run this if you are okay with losing any existing data.
              </p>
              <v-btn class="ma-0" color="error" @click="showRebuildDialog=true">Rebuild database</v-btn>
              <h3 class="title mb-3 mt-5">Clear data from levy collection</h3>
              <p>
                Clears all in-scope providers, overrides, notices, invoices and history entries that relate 
                to a levy collection so you can simulate a fresh start.
                Note that this will be done quick and dirty - nothing will be logged and no business logic will be applied.
              </p>
              <v-btn class="ma-0" color="primary" @click="showClearLevyCollectionDialog=true">Clear data from a levy collection</v-btn>
              <h3 class="title mb-3 mt-5">Seed invoices to match SAP test data</h3>
              <p>
                The SAP team have entered simulated payments against a set of invoices for us. Running this seeder will
                create matching invoice records the 2019 HELP levy collection which we can then use to test our SAP reconciliation. 
                The levy collection providers for the invoicing scenarios will be marked as favourites so you can find them.
              </p>
              <p>
                Before running this seeder, ensure that the 2019 HELP levy collection:
                <ul>
                  <li>Has had in-scope providers imported;</li>
                  <li>Has had levy calculation parameters imported; and</li>
                  <li>Is at the <i>Invoicing</i> status.</li>
                </ul>
              </p>
              <p>
                Note that this will be done quick and dirty - logging will be minimal and no business logic will be applied.
              </p>
              <v-btn class="ma-0" color="primary" @click="showSeedSampleInvoicesDialog=true">Seed sample invoices</v-btn>
            </v-card-text>
            <rebuild-dialog v-model="showRebuildDialog"/>
            <clear-levy-collection-dialog v-model="showClearLevyCollectionDialog"/>
            <seed-sample-invoices-dialog v-model="showSeedSampleInvoicesDialog" />
          </v-card>
        </v-tab-item>
        <v-tab-item key="theme" transition="none" reverse-transition="none">
          <v-card flat>
            <v-card-text style="height: 380px;">
              <p>
                Try out some different themes!  
              </p>
              <p>
                <strong>Note:</strong>
                This is just to demonstrate that the application can be made to look a bit different depending on some base theme colours.  Normally the colour selections would be based on related branding.  At the moment, we have the following 3 settings:
              </p>
              <table class="v-datatable v-table theme--light">
                <thead>
                  <tr>
                    <th class="column text-xs-left">Setting</th>
                    <th class="column text-xs-left">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Primary</strong></td>
                    <td>Top toolbar, links, primary buttons, etc.</td>
                  </tr>
                  <tr>
                    <td><strong>Secondary</strong></td>
                    <td>Dialog titles and other miscellaneous areas.</td>
                  </tr>
                  <tr>
                    <td><strong>Accent</strong></td>
                    <td>Left navigation panel.</td>
                  </tr>
                </tbody>
              </table>
              <p class="mt-5 mb-2 text-xs-center">
                <v-btn class="ml-0" dark color="#78559A" @click="themeDefault">Purple</v-btn>
                <v-btn class="ml-0" dark color="#546E7A" @click="themeBlueGrey">Blue Grey</v-btn>
                <v-btn class="ml-0" dark color="#8D6E63" @click="themeBrown">Brown</v-btn>
                <v-btn class="ml-0" dark color="#0097A7" @click="themeLynn">Lynn Special</v-btn>
                <v-btn class="ml-0" dark color="#5B444B" @click="themePurpblgrn">Purpblgrn</v-btn>
                <v-btn class="ml-0" dark color="#f26b3a" @click="themePostman">Postman</v-btn>
                <v-btn class="ml-0" dark color="#3182BD" @click="themeVsCode">VS Code</v-btn>
                <v-btn class="ml-0" dark color="#414A59" @click="themeManjaro">Manjaro</v-btn>
              </p>
            </v-card-text>
          </v-card>
        </v-tab-item>
        <v-tab-item key="users" transition="none" reverse-transition="none">
          <v-card flat>
            <v-card-text>
              <prisms-user-listing ref="listing" show-actions embedded />
            </v-card-text>
          </v-card>
        </v-tab-item>
        <v-tab-item key="other" transition="none" reverse-transition="none">
          <v-card flat>
            <v-card-text>
              <h3>
                Temporarily disable testing tools
              </h3>
              <p>
                Click the button to simulate testing tools being disabled,
                so testing can be conducted to ensure the system behaves as expected.
                This will just change the front-end behaviour, not server behaviour.
                Refreshing your browser will re-enable testing tools.
              </p>
              <p class="mb-4">
                <v-btn class="ma-0" @click="disableTestingTools">Disable testing tools</v-btn>
              </p>
              <h3>Reset Vuex store</h3>
              <p>
                This will reset the Vuex store to an initial state plus current user and system details, which effectively resets the web application state.  Refreshing your browser does roughly the same thing.
              </p>
              <p class="mb-4">
                <v-btn class="ma-0" @click="resetVuexStore">Reset Vuex store</v-btn>
              </p>
              <h3>Our action buttons</h3>
              <our-action-button 
                :saving="saving" 
                flat small icon="star_border" color="primary"
                @click="saving=true"
              >
                Start saving something
              </our-action-button>
              <our-action-button 
                :saving="saving" icon="star"
                @click="saving=true"
              >
                Start saving something else
              </our-action-button>
              <v-btn @click="saving=false">Stop saving</v-btn>
              <h3>Error handling</h3>
              <p>Click the button to throw an exception.</p>
              <v-btn class="ma-0" @click="throwException">Throw an exception</v-btn>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs>
    </v-card>
  </div>
</template>

<script>
import RebuildDialog from "@/components/system/RebuildDialog.vue";
import ClearLevyCollectionDialog from "@/components/system/ClearLevyCollectionDialog.vue";
import SeedSampleInvoicesDialog from "@/components/system/SeedSampleInvoicesDialog.vue";
import PrismsUserListing from "@/components/prisms-users/PrismsUserListing.vue";
import { DISABLE_TESTING_TOOLS } from "@/store/system/mutation-types";
import { SHOW_NOTIFICATION } from "@/store/notifications/mutation-types";
import * as store from "@/store";
import * as mutation from "@/store/prisms-users/mutation-types";
import * as exceptionAction from "@/store/exception-logs/action-types";

export default {
  name: "TestingTools",
  components: {
    RebuildDialog,
    ClearLevyCollectionDialog,
    SeedSampleInvoicesDialog,
    PrismsUserListing
  },
  data() {
    return {
      activeTab: 0,
      showRebuildDialog: false,
      showClearLevyCollectionDialog: false,
      showSeedSampleInvoicesDialog: false,
      criteriaMutation: mutation.LIST_SET_CRITERIA,
      oldCode: JSON.stringify({
        someObject: "With values",
        someArray: ["Item 1", "Item 2"]
      }),
      newCode: JSON.stringify({
        someObject: "With changed values",
        someArray: ["Item 1", "Item 2", "Extra Item"]
      }),
      breadcrumbs: this.$store.getters.isSignedIn ? [{ text: "Testing tools" }] : [],
      saving: false
    };
  },
  computed: {
    css() {
      return this.isSignedIn ? "home" : "home ma-4";
    },
    isSignedIn() {
      return this.$store.getters.isSignedIn;
    }
  },
  methods: {
    disableTestingTools() {
      this.$store.commit(DISABLE_TESTING_TOOLS);
      this.$router.push("/");
    },
    resetVuexStore() {
      store.resetState();
      this.$store.commit(SHOW_NOTIFICATION, "Vuex store reset.");
    },
    throwException() {
      this.$store.dispatch(exceptionAction.THROW_EXCEPTION);
    },
    themeDefault() {
      this.$vuetify.theme.primary = "#926FB4";
      this.$vuetify.theme.secondary = "#78559A";
      this.$vuetify.theme.accent = "#f2eef6";
    },
    themeBlueGrey() {
      this.$vuetify.theme.primary = "#87A1AD";
      this.$vuetify.theme.secondary = "#546E7A";
      this.$vuetify.theme.accent = "#dce3e7";
    },
    themeBrown() {
      this.$vuetify.theme.primary = "#B09186";
      this.$vuetify.theme.secondary = "#7D5E53";
      this.$vuetify.theme.accent = "#f5f1f0";
    },
    themeLynn() {
      this.$vuetify.theme.primary = "#E69E34";
      this.$vuetify.theme.secondary = "#0097A7";
      this.$vuetify.theme.accent = "#F5F5F5";
    },
    themePurpblgrn() {
      this.$vuetify.theme.primary = "#5ba23a";
      this.$vuetify.theme.secondary = "#5B444B";
      this.$vuetify.theme.accent = "#ffffff";
    },
    themePostman() {
      this.$vuetify.theme.primary = "#f26b3a";
      this.$vuetify.theme.secondary = "#303030";
      this.$vuetify.theme.accent = "#ffffff";
    },
    themeVsCode() {
      this.$vuetify.theme.primary = "#3182BD";
      this.$vuetify.theme.secondary = "#262F3B";
      this.$vuetify.theme.accent = "#f3f3f3";
    },
    themeManjaro() {
      this.$vuetify.theme.primary = "#16A086";
      this.$vuetify.theme.secondary = "#303641";
      this.$vuetify.theme.accent = "#EDEDED";
    }
  }
};
</script>
